import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import rankingService, { RecentRankingItem } from '../../services/rankingService';
import RankingPreviewCard from '../rankings/RankingPreviewCard';
import { t } from '../../i18n';

// ============================================
// „Aktuelle Ranglisten“ – horizontale Scroll-Sektion auf der Startseite
// (Marcel 2026-10-20): zuletzt aktualisierte Listing-Ranglisten (Ligen,
// Vereine, Verbände, Spielstätten) als Karten im Stil von „Darts-Wissen“.
// Karten sind ECHTE Links (<a href>) → SEO: die Ranglisten-Seiten hängen
// direkt an der Startseite. Bewusst OHNE „Alle“-Link (Marcel 2026-10-20:
// /ranglisten sind die Profi-Ranglisten). Blendet sich bei leeren Daten aus.
// Seit 22.10.2026: Karte = gemeinsame RankingPreviewCard (auch auf den Listing-Seiten).
// ============================================

const MAX_ITEMS = 12;

const HomeRecentRankings: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<RecentRankingItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    rankingService
      .recent(MAX_ITEMS)
      .then((list) => { if (!cancelled) setItems(list); })
      .catch(() => { /* Section bleibt ausgeblendet */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const scrollBy = (delta: number) => scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });

  if (!loading && items.length === 0) return null;

  return (
    <section aria-label={t('web.component.home-recent-rankings.titel')} data-testid="home-recent-rankings">
      <div className="mb-3 flex items-center justify-between">
        <div className="min-w-0">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.home-recent-rankings.titel')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">{t('web.component.home-recent-rankings.untertitel')}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {!loading && items.length > 3 && (
            <>
              <button type="button" aria-label={t('web.component.home-recent-rankings.zurueck')} onClick={() => scrollBy(-360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex">
                <ChevronLeft size={15} />
              </button>
              <button type="button" aria-label={t('web.component.home-recent-rankings.weiter')} onClick={() => scrollBy(360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex">
                <ChevronRight size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-[200px] w-[240px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900" />
            ))
          : items.map((item) => <RankingPreviewCard key={item.id} item={item} testId="home-recent-ranking-card" />)}
      </div>
    </section>
  );
};

export default HomeRecentRankings;
